import { JobProgress } from '@app/components/base/JobProgress';
import { auditReports, videoEditor, webApi } from '@mi-front/api-clients';
import SettingsIcon from '@mui/icons-material/Settings';
import { Box, Stack, keyframes } from '@mui/material';
import { useIntl } from 'react-intl';
import { publishMessages } from '../publish-messages';
import { UploadFailedErrorDetailsIcon } from './UploadFailedErrorDetailsIcon';
import { UploadCardDto } from './useUploadCardActions';

interface UploadStatusChipProps {
  /** Full BFF upload row; `job` snapshot (or upload status fallback) drives JobProgress. */
  upload: UploadCardDto;
  /**
   * When true and status is WAITING, shows "Esperando media" instead of "En curso"
   * (deferred upload waiting for fragment assets or edit render).
   */
  waitingForMedia?: boolean;
}

/**
 * Resolves the job passed to JobProgress: prefer the BullMQ snapshot, else a stub from upload status.
 */
function resolveUploadStatusJob(
  upload: UploadCardDto,
): auditReports.PublicJobDto | videoEditor.PublicJobWithResultDto {
  if (upload.job) {
    return upload.job as videoEditor.PublicJobWithResultDto;
  }

  return {
    id: upload.id,
    status: upload.status,
    isPending: upload.isPending,
    isFinished: !upload.isPending,
    processedOn: upload.createdAt,
  } as auditReports.PublicJobDto;
}

const rotateKeyframes = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

/**
 * Social upload status chip via JobProgress for every status, with publish-specific labels.
 * Failed uploads also show an error-details icon with tooltip when available.
 */
export function UploadStatusChip({ upload, waitingForMedia = false }: UploadStatusChipProps) {
  const { $t } = useIntl();
  const { status } = upload;

  if (!status) {
    return null;
  }

  const inProgressLabel = $t(publishMessages.chipInProgress);
  const isWaitingForMedia = waitingForMedia && status === 'WAITING';
  const waitingLabel = isWaitingForMedia
    ? $t(publishMessages.chipWaitingForMedia)
    : inProgressLabel;

  const jobProgress = (
    <JobProgress
      job={resolveUploadStatusJob(upload)}
      size="small"
      variant="filled"
      statusLabels={{
        SCHEDULED: $t(publishMessages.chipScheduled),
        WAITING: waitingLabel,
        WAITING_CHILDREN: inProgressLabel,
        IN_PROGRESS: inProgressLabel,
        COMPLETED: $t(publishMessages.chipPublished),
        FAILED: $t(publishMessages.chipError),
        CANCELLED: $t(publishMessages.chipCancelled),
      }}
      statusIcons={
        isWaitingForMedia
          ? {
              WAITING: (
                <Box
                  sx={{
                    display: 'flex',
                    animation: `${rotateKeyframes} 3s linear infinite`,
                  }}
                >
                  <SettingsIcon fontSize="small" />
                </Box>
              ),
            }
          : undefined
      }
    />
  );

  if (status === 'FAILED') {
    const failedData =
      upload.data?.status === 'FAILED' ? (upload.data as webApi.UploadFailedDataDto) : undefined;

    return (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {jobProgress}
        <UploadFailedErrorDetailsIcon error={failedData?.error} errorCode={failedData?.errorCode} />
      </Stack>
    );
  }

  return jobProgress;
}
